import { CollectDataStepStatus, CollectDataStepType } from 'src/constants/collect-data/enums'
import { StepAction } from 'src/constants/partnerDriver/enums'
import {
  TERMINAL_STEP_STATUSES,
  UPLOADABLE_STEP_TYPES,
} from 'src/constants/partnerDriver/constants'
import type { PartnerDriverStep, StepActionPermissions } from 'src/interfaces/partnerDriver'

// Upload and delete-all hit `/contractors/steps/:stepId/document` (`contractor_documents_manage`);
// every other action is a `/contractors/steps/:stepId/*` step mutation (`contractor_steps_manage`).
const DOCUMENT_STEP_ACTIONS: StepAction[] = [StepAction.UploadDocuments, StepAction.DeleteDocuments]

/**
 * Which actions the steps table offers for a step — spec §12.4. Visibility follows what the
 * backend accepts; where Retool was narrower for an action it had (upload types, complete
 * created) Retool wins. Backend-only guards (e.g. a driver licence needs an earlier completed one
 * with the same label) are not duplicated — their 400 message reaches the error toast.
 *
 * Actions the signed-in internal user's permissions would refuse are left out (ENG-1207 part 2).
 */
export const getStepActions = (
  step: PartnerDriverStep,
  { canManageSteps, canManageDocuments }: StepActionPermissions,
): StepAction[] => {
  const { type, status } = step
  const isTerminal = TERMINAL_STEP_STATUSES.includes(status)
  const documentsCount = step.partnerDriverCollectedData?.documents?.length ?? 0
  const actions: StepAction[] = []

  if (
    (type === CollectDataStepType.BGC || type === CollectDataStepType.MVR) &&
    status === CollectDataStepStatus.Created
  ) {
    actions.push(StepAction.CompleteBackgroundCheck)
  }

  if (
    type === CollectDataStepType.VehicleInsurance &&
    status === CollectDataStepStatus.Created &&
    !step.metadata?.disableInsuranceUpload
  ) {
    actions.push(StepAction.CompleteVehicleInsurance)
  }

  if (type === CollectDataStepType.DriverLicense && !isTerminal) {
    actions.push(StepAction.CompleteDriverLicense)
  }

  if (type === CollectDataStepType.LMSTraining && !isTerminal) {
    actions.push(StepAction.CompleteLmsTraining)
  }

  if (type === CollectDataStepType.InsuranceDeclaration && !isTerminal) {
    actions.push(StepAction.CompleteInsuranceDeclaration)
  }

  // Both backend routes accept a `created` step only.
  if (
    type === CollectDataStepType.VehicleRegistration &&
    status === CollectDataStepStatus.Created
  ) {
    actions.push(StepAction.CompleteVehicleRegistration)
  }

  if (type === CollectDataStepType.DOTNumber && status === CollectDataStepStatus.Created) {
    actions.push(StepAction.CompleteDotNumber)
  }

  if (
    status === CollectDataStepStatus.Failed &&
    type !== CollectDataStepType.BGC &&
    type !== CollectDataStepType.DriverLicense
  ) {
    actions.push(StepAction.CompleteFailed)
  }

  if (
    UPLOADABLE_STEP_TYPES.includes(type) &&
    status !== CollectDataStepStatus.CompletedNotActive &&
    status !== CollectDataStepStatus.Archived
  ) {
    actions.push(StepAction.UploadDocuments)
  }

  if (status !== CollectDataStepStatus.Archived) {
    actions.push(StepAction.Reset)
  }

  if (documentsCount > 0) {
    actions.push(StepAction.DeleteDocuments)
  }

  return actions.filter((action) => {
    if (DOCUMENT_STEP_ACTIONS.includes(action)) {
      return canManageDocuments
    }

    return canManageSteps
  })
}
